import React, { useMemo, useState } from 'react';
import { X, ArrowsLeftRight, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';

// Edit Assignments dialog (admin/EM only).
//
// In-memory swap engine: keeps a `local` shape of `[ {court_number, slots: [registration]} ]`
// matching the server's slot ordering. The "Move" action on a player tile
// opens a small picker (target court + target position) and SWAPS the two
// slots. This guarantees the registration set is preserved at all times,
// so the validation panel only needs to confirm court counts (always true
// after a swap) — the trickier "missing/duplicate" rules can't be violated.
//
// Save → PATCH /api/event-sessions/:id/weeks/:n/assignments with the full
// layout. Backend re-validates and writes.
export default function EditAssignmentsDialog({ open, onClose, session, week, scoringPresent = false, onSaved }) {
  const initial = useMemo(() => {
    if (!week) return [];
    return week.courts.map((c) => ({
      court_number: c.court_number,
      slots: c.slots.map((s) => ({
        registration_id: s.registration_id,
        name: s.display_name || s.player_name || s.name || '—',
        usta_rating: s.usta_rating || null,
      })),
    }));
  }, [week]);

  const [local, setLocal] = useState(initial);
  const [picker, setPicker] = useState(null); // {fromCourt, fromIdx, targetCourt, targetIdx}
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  // Reset local whenever the dialog re-opens with new week data.
  React.useEffect(() => { setLocal(initial); setConfirmSave(false); }, [initial, open]);

  if (!open || !week) return null;

  const playersPerCourt = session?.players_per_court || (local[0]?.slots?.length ?? 0);

  const swap = (a, b) => {
    setLocal((prev) => {
      const next = prev.map((c) => ({ ...c, slots: [...c.slots] }));
      const A = next.find((c) => c.court_number === a.court);
      const B = next.find((c) => c.court_number === b.court);
      if (!A || !B) return prev;
      const tmp = A.slots[a.idx];
      A.slots[a.idx] = B.slots[b.idx];
      B.slots[b.idx] = tmp;
      return next;
    });
  };

  const openPicker = (court_number, idx) => {
    setPicker({ fromCourt: court_number, fromIdx: idx, targetCourt: court_number, targetIdx: idx });
  };

  const confirmMove = () => {
    if (!picker) return;
    if (picker.fromCourt === picker.targetCourt && picker.fromIdx === picker.targetIdx) {
      setPicker(null);
      return;
    }
    swap({ court: picker.fromCourt, idx: picker.fromIdx },
         { court: picker.targetCourt, idx: picker.targetIdx });
    setPicker(null);
  };

  const isDirty = JSON.stringify(local) !== JSON.stringify(initial);

  const performSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const courts = local.map((c) => ({
        court_number: c.court_number,
        registration_ids: c.slots.map((s) => s.registration_id),
      }));
      await eventAPI.editWeekAssignments(session.id, week.week_number, courts);
      toast.success(`Week ${week.week_number} assignments saved.`);
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' ? detail : 'Could not save assignments.');
      toast.error(msg);
    } finally {
      setSaving(false);
      setConfirmSave(false);
    }
  };

  const handleSaveClick = () => {
    if (saving || !isDirty) return;
    if (scoringPresent) {
      setConfirmSave(true);
      return;
    }
    performSave();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center" data-testid="edit-assignments-dialog">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl max-h-modal overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#e3dcd1] flex-shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#6a766e]">Edit Assignments</div>
            <h2 className="font-serif text-xl text-[#013523]">Week {week.week_number}</h2>
          </div>
          <button onClick={onClose} data-testid="edit-assignments-close" className="p-1.5 text-[#6a766e] hover:text-[#013523]">
            <X size={18} />
          </button>
        </header>

        <div className="p-3 text-xs text-[#6a766e] bg-[#fbfaf8] border-b border-[#e3dcd1] flex-shrink-0">
          Tap <b>Move</b> on a player tile, then pick a target court and position. The two slots swap so the layout stays valid.
        </div>

        {scoringPresent && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-start gap-2 flex-shrink-0" data-testid="edit-assignments-scoring-warning">
            <Warning size={16} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900">
              <div className="font-medium">Scores, subs, or no-shows already exist for this week.</div>
              <div className="mt-0.5">Editing assignments may affect score entry and movement calculations. Each player&apos;s existing points / sub / no-show / notes follow them to their new court. Continue carefully.</div>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 grid gap-3 sm:grid-cols-2">
          {local.map((c) => (
            <section
              key={c.court_number}
              data-testid={`edit-assign-court-${c.court_number}`}
              className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 space-y-2"
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6a766e]">Court</div>
                  <div className="font-serif text-xl text-[#013523]">{c.court_number}</div>
                </div>
                <div className="text-[10px] text-[#6a766e]">
                  {c.slots.length}/{playersPerCourt}
                </div>
              </div>
              <ul className="space-y-1.5">
                {c.slots.map((s, idx) => (
                  <li
                    key={`${c.court_number}-${idx}`}
                    data-testid={`edit-assign-slot-${c.court_number}-${idx}`}
                    className="flex items-center justify-between gap-2 bg-white border border-[#e3dcd1] rounded-lg px-2.5 py-1.5"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-[#013523] truncate">{s.name}</div>
                      {s.usta_rating && <div className="text-[10px] text-[#6a766e]">USTA {s.usta_rating}</div>}
                    </div>
                    <button
                      data-testid={`edit-assign-move-${c.court_number}-${idx}`}
                      onClick={() => openPicker(c.court_number, idx)}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] bg-[#013523] hover:bg-[#011e15] text-white rounded-full px-2.5 py-1"
                    >
                      <ArrowsLeftRight size={11} /> Move
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-[#e3dcd1] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
          <div className="text-[11px] text-[#6a766e]">
            {isDirty ? 'Unsaved changes' : 'No changes yet'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="edit-assignments-cancel" className="text-sm text-[#013523] px-3 py-1.5 rounded-full border border-[#e3dcd1] hover:bg-[#fbfaf8]">
              Cancel
            </button>
            <button
              data-testid="edit-assignments-save"
              onClick={handleSaveClick}
              disabled={saving || !isDirty}
              className={`text-sm rounded-full px-4 py-1.5 ${saving || !isDirty ? 'bg-[#a0aaa4] text-white cursor-not-allowed' : 'bg-[#015e3f] hover:bg-[#014d34] text-white'}`}
            >
              {saving ? 'Saving…' : 'Save Assignments'}
            </button>
          </div>
        </footer>
      </div>

      {picker && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="edit-assignments-move-picker">
          <div className="bg-white rounded-2xl max-w-xs w-full p-4 space-y-3">
            <div className="text-sm font-medium text-[#013523]">
              Move {local.find(c => c.court_number === picker.fromCourt)?.slots[picker.fromIdx]?.name}
            </div>
            <label className="block text-xs">
              <span className="text-[#6a766e]">Target court</span>
              <select
                data-testid="picker-target-court"
                value={picker.targetCourt}
                onChange={(e) => setPicker({ ...picker, targetCourt: Number(e.target.value), targetIdx: 0 })}
                className="mt-1 w-full border border-[#e3dcd1] rounded-md px-2 py-1.5 text-sm"
              >
                {local.map((c) => (
                  <option key={c.court_number} value={c.court_number}>Court {c.court_number}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs">
              <span className="text-[#6a766e]">Target position</span>
              <select
                data-testid="picker-target-position"
                value={picker.targetIdx}
                onChange={(e) => setPicker({ ...picker, targetIdx: Number(e.target.value) })}
                className="mt-1 w-full border border-[#e3dcd1] rounded-md px-2 py-1.5 text-sm"
              >
                {(local.find(c => c.court_number === picker.targetCourt)?.slots || []).map((s, idx) => (
                  <option key={idx} value={idx}>#{idx + 1} — {s.name}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPicker(null)} data-testid="picker-cancel" className="text-xs text-[#013523] px-3 py-1.5 rounded-full border border-[#e3dcd1]">
                Cancel
              </button>
              <button data-testid="picker-confirm" onClick={confirmMove} className="text-xs bg-[#015e3f] hover:bg-[#014d34] text-white rounded-full px-3 py-1.5">
                Swap
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmSave && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="edit-assignments-confirm-save">
          <div className="bg-white rounded-2xl max-w-sm w-full p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Warning size={20} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium text-[#013523]">Confirm assignment changes</div>
                <div className="text-xs text-[#6a766e] mt-1">
                  Scores, subs, or no-shows already exist for this week. Editing assignments may affect score entry and movement calculations. Continue carefully.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                data-testid="confirm-save-cancel"
                onClick={() => setConfirmSave(false)}
                className="text-xs text-[#013523] px-3 py-1.5 rounded-full border border-[#e3dcd1]"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-save-continue"
                onClick={performSave}
                disabled={saving}
                className="text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1.5 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Continue Editing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
